import { useMemo } from "react"
import { Household, LexicalText } from "@/schema"
import { Button, Card } from "@ui/shad"
import { cn } from "@ui/utils"
import { format } from "date-fns"
import { Check, Plus } from "lucide-react"

import { useActionLink, useExecuteActionLink } from "@/hooks/api/action-links"
import { useFetchBusiness } from "@/hooks/api/businesses"
import { useFetchContact } from "@/hooks/api/contacts"
import { useFetchHousehold } from "@/hooks/api/households"
import { markdownToNodes } from "@/components/lexical/serializer"
import { LexicalPreview } from "@/components/tasks/lexical-preview"
import { useCreateTaskComponent } from "@/components/tasks/use-create-task-component"

const toLexical = (text?: string): LexicalText | undefined => {
  if (!text) return undefined
  const nodes = markdownToNodes(text) || undefined
  return { markdown: text, nodes, preferred_format: "markdown" }
}

interface CreateTaskProps {
  id: string
}

export default function CreateTask({ id }: CreateTaskProps) {
  const { data: actionLink } = useActionLink(id)

  const householdId = actionLink?.properties?.household_id ? String(actionLink.properties.household_id) : ""
  const taskableType = actionLink?.properties?.taskable_type
  const taskableId = actionLink?.properties?.taskable_id ? String(actionLink.properties.taskable_id) : ""
  const { data: household, isLoading: isHouseholdLoading } = useFetchHousehold(householdId, {
    enabled: !!householdId,
  })
  const { data: contact, isLoading: isContactLoading } = useFetchContact(taskableType === "Contact" ? taskableId : null)
  const { data: business, isLoading: isBusinessLoading } = useFetchBusiness(
    taskableType === "Business" ? taskableId : null
  )

  const { mutate: execute, isPending, isSuccess } = useExecuteActionLink()
  const { open } = useCreateTaskComponent()

  const taskData = useMemo(() => {
    if (!actionLink?.properties) return null

    // deadline_at arrives as a Date when it's a valid ISO string (the query provider revives ISO
    // strings into Dates) but stays a raw string for malformed values like "null". Normalize to a
    // valid ISO string (or omit it) so neither the card nor the create-task dialog ever formats an
    // invalid date and throws.
    const rawDeadline = actionLink.properties.deadline_at
    const parsedDeadline =
      rawDeadline instanceof Date ? rawDeadline : typeof rawDeadline === "string" ? new Date(rawDeadline) : null
    const deadline_at =
      parsedDeadline && !Number.isNaN(parsedDeadline.getTime()) ? parsedDeadline.toISOString() : undefined

    return {
      title: toLexical(typeof actionLink.properties.title === "string" ? actionLink.properties.title : undefined),
      description: toLexical(
        typeof actionLink.properties.description === "string" ? actionLink.properties.description : undefined
      ),
      deadline_at,
      household_id: actionLink.properties.household_id as string | number | undefined,
      assigned_to_id: actionLink.properties.assigned_to_id as string | undefined,
    }
  }, [actionLink])

  if (!actionLink || !taskData) return null

  const isExpired = new Date(actionLink.expires_at) < new Date()
  const isExecuted = !!actionLink.executed_at || isSuccess
  const isTaskableLoading = isHouseholdLoading || isContactLoading || isBusinessLoading
  const isTaskableUnavailable =
    (taskableType === "Contact" && !isContactLoading && !contact) ||
    (taskableType === "Business" && !isBusinessLoading && !business)
  const isDisabled = isPending || isExpired || isExecuted || isTaskableLoading || isTaskableUnavailable

  const handleClick = () => {
    if (isDisabled) return

    open({
      prefillData: taskData,
      defaultHousehold: household
        ? (household as Household)
        : taskData.household_id
          ? ({ id: taskData.household_id } as Household)
          : undefined,
      defaultContact: contact,
      defaultBusiness: business,
      onCreated: () => {
        if (!isPending) {
          execute({ id })
        }
      },
    })
  }

  const label = (() => {
    if (isTaskableLoading) return "Loading..."
    if (isTaskableUnavailable) return "Record unavailable"
    if (isPending) return "Creating..."
    if (isExecuted) return "Task created"
    if (isExpired) return "Task expired"
    return "Create task"
  })()

  const buttonVariant = isExecuted || isExpired ? "secondary" : "outline"

  const buttonClassName = cn(
    "transition-all",
    isExecuted && "cursor-default border-green-border bg-green-background text-green-foreground hover:bg-green-border",
    isExpired && "cursor-default border-tertiary bg-tinted text-muted-foreground hover:bg-tinted"
  )

  const icon = isExecuted ? <Check /> : <Plus />

  const formattedDeadline = taskData.deadline_at ? format(new Date(taskData.deadline_at), "MMM d, yyyy") : null

  return (
    <Card className="mt-2 flex w-full max-w-md flex-col gap-2 p-4 shadow-sm">
      <div>
        <h3 className="text-base font-medium">
          {actionLink.properties?.title && typeof actionLink.properties.title === "string" ? (
            <LexicalPreview content={toLexical(actionLink.properties.title)} />
          ) : (
            "Untitled Task"
          )}
        </h3>
        {formattedDeadline ? <p className="text-muted-foreground text-xs">Due {formattedDeadline}</p> : null}
      </div>

      <Button variant={buttonVariant} onClick={handleClick} disabled={isDisabled} className={buttonClassName} size="sm">
        {icon}
        {label}
      </Button>
    </Card>
  )
}
